import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, PropsWithoutRef, MouseEvent } from 'react';

import VisuallyHiddenText from '../VisuallyHiddenText';
import Actions from '../Actions';
import Button from '../Button';
import type { Action, ForwardRefForwardPropsComponent } from '../../types';
import {
  useBreakpoint,
  useConfiguration,
  useConsolidatedRef,
  useElement,
  useI18n,
  useTestIds,
  useUID
} from '../../hooks';
import Flex from '../Flex';
import SearchInput from '../SearchInput';
import Icon, { registerIcon } from '../Icon';
import * as filterIcon from '../Icon/icons/filter.icon';
import * as arrowUpDownIcon from '../Icon/icons/arrow-up-down.icon';
import * as rowIcon from '../Icon/icons/row.icon';
import * as plusIcon from '../Icon/icons/plus.icon';
import * as moreIcon from '../Icon/icons/more.icon';
import * as wrapIcon from '../Icon/icons/wrap.icon';
import * as unwrapIcon from '../Icon/icons/unwrap.icon';
import { hasProp, withTestIds } from '../../utils';
import { formatNumber } from '../Number';
import MenuButton from '../MenuButton';
import Avatar from '../Avatar';
import AdditionalInfo from '../AdditionalInfo';
import FullscreenButton from '../Fullscreen/FullscreenButton';
import Tooltip from '../Tooltip';
import Text from '../Text';

import PresetMenuPopover from './PresetMenuPopover';
import type { PresetMenuPopoverProps } from './PresetMenuPopover';
import {
  StyledViewSelector,
  StyledSearchForm,
  StyledListToolbar,
  StyledHeading,
  StyledCountMeta,
  StyledListToolbarContent,
  StyledListToolbarDialog,
  StyledErrorIcon,
  StyledInfo,
  StyledSearchContainer,
  StyledExpandSearchButton
} from './ListToolbar.styles';
import type {
  ListToolbarProps,
  PresetMenuProps,
  QueryOptionDialogProps,
  QueryOptionId
} from './ListToolbar.types';
import helpers from './helpers';
import { getListToolbarTestIds } from './ListToolbar.test-ids';

registerIcon(filterIcon, arrowUpDownIcon, rowIcon, plusIcon, moreIcon, wrapIcon, unwrapIcon);

const CountMeta = ({ count, ...restProps }: { count: NonNullable<ListToolbarProps['count']> }) => {
  const { locale } = useConfiguration();
  const t = useI18n();
  const totalFormattedValue = formatNumber(count.total ?? 0, {
    locale,
    options: {
      useGrouping: true
    }
  });
  return (
    <StyledCountMeta variant='default' {...restProps}>
      {count.selected
        ? t(
            'selected_count',
            [formatNumber(count.selected, { locale, options: { useGrouping: true } })],
            { count: count.selected }
          )
        : t(
            'results_count',
            [count.totalHasMore ? `${totalFormattedValue}+` : totalFormattedValue],
            {
              count: count.total
            }
          )}
    </StyledCountMeta>
  );
};

const ListToolbar: ForwardRefForwardPropsComponent<ListToolbarProps> = forwardRef(
  function ListToolbar(
    {
      testId,
      name,
      showName = true,
      headingTag = 'h2',
      viewSelector,
      formControlProps,
      createNew,
      search,
      count,
      wrap,
      additionalActions,
      filter,
      sort,
      group,
      actions,
      actionsButtonRef,
      ...restProps
    }: PropsWithoutRef<ListToolbarProps>,
    ref: ListToolbarProps['ref']
  ) {
    const uid = useUID();
    const t = useI18n();
    const testIds = useTestIds(testId, getListToolbarTestIds);

    const consolidatedActionsButtonRef = useConsolidatedRef(actionsButtonRef);
    const toolbarContentRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchButtonRef = useRef<HTMLButtonElement>(null);
    const [headingEl, setHeadingEl] = useElement<HTMLHeadingElement>();
    const [labelEl, setLabelEl] = useElement<HTMLButtonElement>();
    const [isSearchOpen, setIsSearchOpen] = useState(!!search?.value);
    const isSmallOrAbove = useBreakpoint('sm', { breakpointRef: toolbarContentRef });

    useEffect(() => {
      if (search?.value) {
        setIsSearchOpen(true);
      }
    }, [search?.value]);

    useEffect(() => {
      if (!isSearchOpen || search?.value) return;
      const openedSearch = searchInputRef.current?.parentElement;
      if (!openedSearch) return;

      const handleFocusOut = (e: FocusEvent) => {
        if (!search?.value && !openedSearch.contains(e.relatedTarget as HTMLElement)) {
          setIsSearchOpen(false);
        }
      };

      openedSearch.addEventListener('focusout', handleFocusOut);

      return () => {
        openedSearch.removeEventListener('focusout', handleFocusOut);
      };
    }, [isSearchOpen, search?.value]);

    const [currentQueryOptionId, setCurrentQueryOptionId] = useState<QueryOptionId | null>(null);
    const queryOptionPopoverTarget = useRef<HTMLButtonElement>();

    const [actionsQueryOptionPreset, setActionsQueryOptionPreset] =
      useState<Pick<PresetMenuPopoverProps, 'heading' | 'menu'>>();
    const [optionPresetMenuOpen, setOptionPresetMenuOpen] = useState(false);

    const createNewButton = useMemo(() => {
      if (!createNew?.onClick) return null;
      const text = createNew.label ?? t('create_new');

      return (
        <Button data-testid={testIds.createNew} icon label={text} onClick={createNew.onClick}>
          <Icon name='plus' />
        </Button>
      );
    }, [t, createNew, isSmallOrAbove]);

    const wrapButton = useMemo(() => {
      if (!wrap) return null;

      const text = wrap.enabled ? t('disable_word_wrap') : t('enable_word_wrap');

      return (
        <Button
          data-testid={testIds.wrapButton}
          variant='simple'
          icon
          label={text}
          aria-label={text}
          onClick={wrap.onClick}
        >
          <Icon name={wrap.enabled ? 'unwrap' : 'wrap'} />
        </Button>
      );
    }, [wrap, isSmallOrAbove, t]);

    const searchInput = useMemo(
      () => (
        <SearchInput
          data-testid={testIds.searchInput}
          {...search}
          onSearchSubmit={(value: string) => {
            search?.onSearchSubmit?.(value);
          }}
          onClick={(e: MouseEvent<HTMLInputElement>) => {
            e.preventDefault();
          }}
          ref={searchInputRef}
        />
      ),
      [search]
    );

    const searchLabel = t('search');
    const expandableSearch = useMemo(() => {
      return isSearchOpen ? (
        searchInput
      ) : (
        <StyledExpandSearchButton
          data-testid={testIds.searchButton}
          variant='simple'
          icon
          label={searchLabel}
          aria-label={t('expand_search')}
          onClick={() => {
            setIsSearchOpen(true);
            setTimeout(() => searchInputRef.current?.focus());
          }}
          onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
            if (e.target !== searchInputRef.current && (e.key === 'Enter' || e.key === ' ')) {
              setIsSearchOpen(true);
              setTimeout(() => {
                searchInputRef.current?.focus();
                searchInputRef.current?.select();
              });
            }
          }}
          ref={searchButtonRef}
        >
          <Icon name='search' />
        </StyledExpandSearchButton>
      );
    }, [searchLabel, setIsSearchOpen, isSearchOpen, searchInput]);

    let queryOptionPopover = null;
    if (currentQueryOptionId) {
      const queryOptionProps = { filter, sort, group }[currentQueryOptionId];
      if (
        queryOptionProps &&
        hasProp(queryOptionProps, 'renderer') &&
        queryOptionPopoverTarget.current
      ) {
        queryOptionPopover = (
          <StyledListToolbarDialog
            target={queryOptionPopoverTarget.current}
            heading={t(currentQueryOptionId)}
            placement='bottom-end'
            onCancel={() => {
              if (queryOptionProps.onCancel() === false) return;
              setCurrentQueryOptionId(null);
            }}
            onSubmit={() => {
              if (queryOptionProps.onSubmit() === false) return;
              setCurrentQueryOptionId(null);
            }}
          >
            <queryOptionProps.renderer {...(queryOptionProps.rendererProps || {})} />
          </StyledListToolbarDialog>
        );
      }
    }

    const getQueryOptionAction = useCallback(
      (
        id: 'sort' | 'group' | 'filter',
        icon: string,
        queryOptionProps: QueryOptionDialogProps | PresetMenuProps
      ) => {
        return {
          text: t(id),
          id,
          icon,
          count: queryOptionProps.count,
          onClick: () => {
            if (hasProp(queryOptionProps, 'renderer')) {
              setCurrentQueryOptionId(id);
              queryOptionPopoverTarget.current = consolidatedActionsButtonRef.current ?? undefined;
            }
            if (hasProp(queryOptionProps, 'items')) {
              setActionsQueryOptionPreset({
                heading: t(id),
                menu: {
                  mode: queryOptionProps.mode,
                  items: queryOptionProps.items.map(item => ({ ...item, primary: item.text })),
                  onItemClick: queryOptionProps.onItemClick
                }
              });
              setOptionPresetMenuOpen(true);
            }
          }
        };
      },
      []
    );

    const basicModeActions = useMemo(() => {
      let basicActions: Action[] = [];
      if (filter) {
        const filterAction: Action = getQueryOptionAction('filter', 'filter', filter);
        basicActions.push(filterAction);
      }
      if (sort) {
        const sortAction: Action = getQueryOptionAction('sort', 'arrow-up-down', sort);
        basicActions.push(sortAction);
      }
      if (group) {
        const groupAction: Action = getQueryOptionAction('group', 'row', group);
        basicActions.push(groupAction);
      }
      if (actions) {
        basicActions = basicActions.concat(actions);
      }
      return basicActions.length ? basicActions : undefined;
    }, [actions, filter, sort, group, getQueryOptionAction]);

    const smallScreenActions = useMemo(() => {
      let basicActions: Action[] = [];
      if (createNew && createNew.onClick) {
        const text = createNew.label ?? t('create_new');
        const createNewAction: Action = {
          text,
          id: text,
          icon: 'plus',
          'aria-label': text,
          onClick: (_, e) => createNew.onClick(e),
          'data-testid': testIds.createNew
        };
        basicActions.push(createNewAction);
      }
      if (wrap) {
        const text = wrap.enabled ? t('disable_word_wrap') : t('enable_word_wrap');
        const wrapAction: Action = {
          text,
          id: text,
          icon: wrap.enabled ? 'unwrap' : 'wrap',
          'aria-label': text,
          onClick: (_, e) => wrap.onClick(e),
          'data-testid': testIds.wrapButton
        };
        basicActions.push(wrapAction);
      }
      if (basicModeActions) {
        basicActions = basicActions.concat(basicModeActions);
      }
      return basicActions.length ? basicActions : undefined;
    }, [createNew?.label, createNew?.onClick, wrap?.enabled, wrap?.onClick, basicModeActions]);

    let labelText = name;

    const viewItems = helpers.getViews(viewSelector?.views ?? []);
    const shouldRenderViewSelector =
      viewSelector &&
      (viewSelector.views.length > 1 ||
        (!!viewSelector.views.length && !!viewSelector.onCreateView) ||
        (!!viewSelector.views.length && !!viewSelector.filterInputProps));
    const selectedViewName =
      shouldRenderViewSelector && !!viewItems.length
        ? helpers.getSelectedView(viewSelector.views)?.text ?? viewItems[0].text
        : '';

    if (selectedViewName) {
      labelText = `${name}: ${selectedViewName}`;
    }

    return (
      <Flex
        data-testid={testIds.root}
        container
        {...restProps}
        as={StyledListToolbar}
        ref={ref}
        isSmallOrAbove={isSmallOrAbove}
      >
        <StyledListToolbarContent
          container={{ direction: 'column' }}
          ref={toolbarContentRef}
          item={{ grow: 1 }}
        >
          <Flex
            container={{
              alignItems: 'center',
              justify: 'between',
              direction: 'row'
            }}
          >
            <Flex
              container={{
                alignItems: isSmallOrAbove ? 'center' : undefined,
                justify: 'between',
                direction: isSmallOrAbove ? undefined : 'column'
              }}
              item={{ grow: 1 }}
            >
              <Flex
                container={{
                  alignItems: 'center',
                  gap: 1
                }}
                item={{ grow: 1 }}
              >
                <StyledHeading
                  data-testid={testIds.heading}
                  required={formControlProps?.required}
                  showName={showName}
                  aria-describedby={formControlProps?.info ? `${uid}-info` : undefined}
                >
                  <Text
                    variant={headingTag}
                    ref={setHeadingEl}
                    required={formControlProps?.required}
                  >
                    {name}
                    {shouldRenderViewSelector && <>:&nbsp;</>}
                  </Text>
                  <Tooltip target={headingEl} describeTarget={false} hideDelay='none' smart>
                    {name}
                  </Tooltip>
                  {shouldRenderViewSelector && (
                    <>
                      <MenuButton
                        ref={setLabelEl}
                        data-testid={testIds.viewSelector}
                        variant='link'
                        text={selectedViewName}
                        as={StyledViewSelector}
                        menu={{
                          mode: 'single-select',
                          items: viewSelector.views.map(view => {
                            if (hasProp(view, 'items')) {
                              return {
                                id: view.text,
                                label: view.text,
                                items: view.items.map(groupedView => {
                                  return {
                                    ...groupedView,
                                    primary: groupedView.text,
                                    visual: groupedView.visual ? (
                                      <Avatar {...groupedView.visual} />
                                    ) : undefined
                                  };
                                })
                              };
                            }

                            const { visual, ...restView } = view;

                            return {
                              ...restView,
                              primary: view.text,
                              visual: visual ? <Avatar {...visual} /> : undefined
                            };
                          }),
                          onItemClick: viewSelector.onViewSelect,
                          filterInputProps: viewSelector.filterInputProps,
                          ...(viewSelector.onCreateView &&
                            labelEl && {
                              onCreateNew: {
                                text: t('list_toolbar_add_view'),
                                handler: () => {
                                  viewSelector.onCreateView?.(labelEl);
                                }
                              }
                            })
                        }}
                      />
                      {labelEl && (
                        <Tooltip target={labelEl} describeTarget={false} hideDelay='none' smart>
                          {selectedViewName}
                        </Tooltip>
                      )}
                    </>
                  )}
                </StyledHeading>
                {formControlProps?.additionalInfo && (
                  <AdditionalInfo
                    heading={formControlProps.additionalInfo.heading}
                    contextualLabel={labelText}
                  >
                    {formControlProps.additionalInfo.content}
                  </AdditionalInfo>
                )}
                {isSmallOrAbove && count && <CountMeta data-testid={testIds.count} count={count} />}
              </Flex>
              <>
                {isSmallOrAbove && additionalActions}
                {search && isSmallOrAbove && expandableSearch}
                {isSmallOrAbove && wrapButton}
                {isSmallOrAbove && <FullscreenButton />}
                {isSmallOrAbove && basicModeActions && (
                  <Actions
                    contextualLabel={labelText}
                    items={basicModeActions.map(({ icon, ...restActionProps }) => ({
                      ...restActionProps,
                      visual: icon && <Icon name={icon} />
                    }))}
                    menuAt={1}
                    ref={consolidatedActionsButtonRef}
                  />
                )}
                {isSmallOrAbove && createNewButton}
                {!isSmallOrAbove && count && (
                  <CountMeta data-testid={testIds.count} count={count} />
                )}
              </>
            </Flex>
            {!isSmallOrAbove && (
              <>
                <FullscreenButton />
                {smallScreenActions && (
                  <Actions
                    contextualLabel={labelText}
                    items={smallScreenActions.map(({ icon, ...restActionProps }) => ({
                      ...restActionProps,
                      visual: icon && <Icon name={icon} />
                    }))}
                    menuAt={1}
                    ref={consolidatedActionsButtonRef}
                  />
                )}
              </>
            )}
          </Flex>
          {!isSmallOrAbove && (search || (count && !!count.selected)) && (
            <StyledSearchContainer container={{ alignItems: 'start', justify: 'between' }}>
              <Flex
                as={StyledSearchForm}
                container={{ alignItems: 'stretch', wrap: 'wrap', gap: 1, direction: 'column' }}
                item={{ grow: 1 }}
              >
                {search && searchInput}
                {count && !!count.selected && additionalActions}
              </Flex>
            </StyledSearchContainer>
          )}
          {formControlProps?.info && (
            <StyledInfo
              role={
                formControlProps.status === 'error' || formControlProps.status === 'warning'
                  ? 'alert'
                  : undefined
              }
              id={`${uid}-info`}
              data-testid={testIds.info}
              status={formControlProps.status}
            >
              {formControlProps.status === 'error' && (
                <>
                  <StyledErrorIcon name='warn-solid' />
                  <VisuallyHiddenText>{` ${t('error')} `}</VisuallyHiddenText>
                </>
              )}
              {formControlProps.info}
            </StyledInfo>
          )}
        </StyledListToolbarContent>

        {queryOptionPopover}

        <PresetMenuPopover
          buttonRef={consolidatedActionsButtonRef}
          heading={actionsQueryOptionPreset?.heading}
          menu={actionsQueryOptionPreset?.menu}
          isOpen={optionPresetMenuOpen}
          onClose={() => setOptionPresetMenuOpen(false)}
        />
      </Flex>
    );
  }
);

export default withTestIds(ListToolbar, getListToolbarTestIds);
